import React, { useState, useEffect, useMemo } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { Auth0Provider } from "@auth0/auth0-react";
import PlayableBoard from "./components/PlayableBoard";
import { apiChecker } from "./utils/gameChecker";
import { recordPlay } from "./utils/playStats";
import AdminPage from "./components/AdminPage";
import Footer from "./components/Footer";
import FeedbackModal from "./components/FeedbackModal";
import axios from "axios";
import HowToPlayModal from "./components/HowToPlayModal";
import NotFoundPage from "./components/NotFoundPage";
import "./App.css";
import { API_BASE } from "./config";



const ICONS = [
  "cookie-fill.svg",
  "ghost-fill.svg",
  "club-fill.svg",
  "finn-the-human-fill.svg",
  // Add more icons for randomization effect
  "star-fill.svg",
  "heart-fill.svg",
  "sun-fill.svg",
  "moon-fill.svg",
  "alien-fill.svg",
  "anchor-fill.svg",
  "alarm-fill.svg",
  "acorn-fill.svg",
  "airplane-fill.svg",
  "apple-logo-fill.svg",
  "app-window-fill.svg",
  "arrow-circle-up-fill.svg",
  "arrow-circle-down-fill.svg",
];

const GameRoute: React.FC<{ onFeedbackClick: () => void }> = ({ onFeedbackClick }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [game, setGame] = useState<any>(null);
  const [showHowTo, setShowHowTo] = useState(false);
  const checker = useMemo(() => (game ? apiChecker(game._id) : null), [game]);

  React.useEffect(() => {
    axios
      .get(`${API_BASE}/api/games/current`)
      .then((res) => {
        // The server only sends the shuffled icons, not the answers
        setGame(res.data);
        setLoading(false);
        recordPlay(res.data._id, { type: "start" });
      })
      .catch(() => {
        setError("No game available for today.");
        setLoading(false);
      });
  }, []);

  if (loading) return <div></div>;
  if (error || !checker) return <div>{error}</div>;

  return (
    <div
      className="app"
      style={{
        textAlign: "center",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        paddingBottom: "60px",
      }}
    >
      <h1 style={{ fontSize: 50 }}>MATCHSET</h1>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <PlayableBoard
          icons={game.iconPool}
          checker={checker}
          startDate={game.startDate}
          nextStartDate={game.nextStartDate}
          onFinish={(r) => recordPlay(game._id, { type: "finish", ...r })}
        />
      </div>
      <HowToPlayModal open={showHowTo} onClose={() => setShowHowTo(false)} />
      <Footer onFeedbackClick={onFeedbackClick} />
    </div>
  );
};

const LandingPage: React.FC<{ onStart: () => void; onFeedbackClick: () => void }> = ({ onStart, onFeedbackClick }) => {
  const [showHowTo, setShowHowTo] = useState(false);
  const [animating, setAnimating] = useState(true);
  const [h1Scale, setH1Scale] = useState(() =>
    window.innerWidth <= 600 ? 1 : 2.67
  );
  const [h1LineHeight, setH1LineHeight] = useState(() =>
    window.innerWidth <= 600 ? 1 : 2.67
  );
  const [iconIndexes, setIconIndexes] = useState([0, 1, 2, 3]);
  const [fadeIn, setFadeIn] = useState(false);
  const [iconSize, setIconSize] = useState(() =>
    window.innerWidth <= 600 ? 60 : 110
  );
  const [iconHeight, setIconHeight] = useState(() =>
    window.innerWidth <= 600 ? 50 : 84
  );
  const [imgSize, setImgSize] = useState(() =>
    window.innerWidth <= 600 ? 30 : 50
  );
  const [gap, setGap] = useState(() =>
    window.innerWidth <= 600 ? 8 : 12
  );
  const [buttonRowHeight, setButtonRowHeight] = useState(0);
  const [fontLoaded, setFontLoaded] = useState(false);
  const buttonRowRef = React.useRef<HTMLDivElement>(null);

  // Animation effect
  useEffect(() => {
    let iconInterval: NodeJS.Timeout;
    let shrinkTimeout: NodeJS.Timeout;
    let finishTimeout: NodeJS.Timeout;
    if (animating) {
      // Randomize icons quickly for 1.2s
      iconInterval = setInterval(() => {
        setIconIndexes([
          Math.floor(Math.random() * ICONS.length),
          Math.floor(Math.random() * ICONS.length),
          Math.floor(Math.random() * ICONS.length),
          Math.floor(Math.random() * ICONS.length),
        ]);
      }, 70);
      // Shrink h1 on desktop, keep same size on mobile after 1.2s
      shrinkTimeout = setTimeout(() => {
        setH1Scale(1);
        setH1LineHeight(1);
        setIconSize(window.innerWidth <= 600 ? 60 : 110);
        setIconHeight(window.innerWidth <= 600 ? 50 : 84);
        setImgSize(window.innerWidth <= 600 ? 30 : 50);
      }, 1200);
      // End animation after 1.7s
      finishTimeout = setTimeout(() => {
        setAnimating(false);
        setIconIndexes([0, 1, 2, 3]); // Set to final icons
        setFadeIn(true);
      }, 1700);
    }
    return () => {
      clearInterval(iconInterval);
      clearTimeout(shrinkTimeout);
      clearTimeout(finishTimeout);
    };
  }, [animating]);

  // Responsive sizes
  useEffect(() => {
    if (!animating) {
      const handleResize = () => {
        if (window.innerWidth <= 600) {
          setH1Scale(1);
          setH1LineHeight(1);
          setIconSize(60);
          setIconHeight(50);
          setImgSize(30);
          setGap(8);
        } else {
          setH1Scale(1);
          setH1LineHeight(1);
          setIconSize(110);
          setIconHeight(84);
          setImgSize(50);
          setGap(12);
        }
      };
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [animating]);

  // Measure button row height after fade-in
  useEffect(() => {
    if (fadeIn && buttonRowRef.current) {
      setButtonRowHeight(buttonRowRef.current.clientHeight);
    }
  }, [fadeIn]);

  // Font loading effect
  useEffect(() => {
    const font = new FontFace(
      "Monigue",
      "url('/fonts/Monigue/Monigue.woff2') format('woff2')"
    );
    font
      .load()
      .then(() => {
        document.fonts.add(font);
        setFontLoaded(true);
      })
      .catch(() => {
        // Fallback to show heading if font fails
        setFontLoaded(true);
      });
    
    // Safari fallback timeout
    const timeout = setTimeout(() => {
      setFontLoaded(true);
    }, 2000);
    
    return () => clearTimeout(timeout);
  }, []);

  const finalIcons = [
    { icon: "cookie-fill.svg", bg: "#9B5DE5" },
    { icon: "ghost-fill.svg", bg: "#3A86FF" },
    { icon: "club-fill.svg", bg: "#00BB7E" },
    { icon: "finn-the-human-fill.svg", bg: "#FFBE0B" },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginTop: 80,
        width: "fit-content",
        margin: "auto",
        height: "100vh",
        justifyContent: "center",
        paddingBottom: "60px",
      }}
    >
      <h1
        className={fontLoaded ? "font-loaded" : "font-loading"}
        style={{
          fontSize: window.innerWidth <= 600 ? 110 : 150,
          transform: `scale(${h1Scale})`,
          transition: "transform 0.5s cubic-bezier(.7,0,.3,1), line-height 0.5s cubic-bezier(.7,0,.3,1)",
          lineHeight: h1LineHeight,
          margin: 0,
          fontFamily: "Monigue, Arial, sans-serif",
          transformOrigin: "center",
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
          backfaceVisibility: "hidden",
        }}
      >
        MATCHSET
      </h1>
      <div
        style={{
          display: "flex",
          gap: gap,
          borderRadius: 200,
          background: "#fff",
          margin: `16px 0 ${fadeIn ? 32 : 48}px 0`,
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          maxWidth: window.innerWidth <= 600 ? "90vw" : "100%",
          transition: "background 0.3s, margin 0.5s cubic-bezier(.7,0,.3,1)",
        }}
      >
        {(animating
          ? iconIndexes.map((idx, i) => ({ icon: ICONS[idx], bg: finalIcons[i].bg }))
          : finalIcons
        ).map((item, i) => (
          <span
            key={i}
            style={{
              width: iconSize,
              height: iconHeight,
              borderRadius: 200,
              background: item.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "border-box",
              userSelect: "none",
              pointerEvents: "none",
              transition: "background 0.3s, width 0.5s cubic-bezier(.7,0,.3,1), height 0.5s cubic-bezier(.7,0,.3,1)",
            }}
          >
            <img
              src={`/icons/${item.icon}`}
              alt={item.icon.replace("-fill.svg", "")}
              style={{
                width: imgSize,
                height: imgSize,
                filter: "brightness(0) invert(1)",
                transition: "width 0.5s cubic-bezier(.7,0,.3,1), height 0.5s cubic-bezier(.7,0,.3,1), filter 0.3s",
              }}
              draggable={false}
            />
          </span>
        ))}
      </div>
      <div
        ref={buttonRowRef}
        style={{
          maxHeight: fadeIn ? 200 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.7s cubic-bezier(.7,0,.3,1), opacity 0.7s cubic-bezier(.7,0,.3,1)',
          display: 'flex',
          gap: 24,
          marginTop: 0,
          opacity: fadeIn ? 1 : 0,
          pointerEvents: fadeIn ? 'auto' : 'none',
        }}
      >
        <button
          className="button-main white-invert how-to-play-desktop"
          onClick={() => setShowHowTo(true)}
        >
          How to play
        </button>
        <button className="button-main" onClick={onStart}>
          Start this week's puzzle
        </button>
      </div>
      <HowToPlayModal open={showHowTo} onClose={() => setShowHowTo(false)} />
      <Footer onFeedbackClick={onFeedbackClick} />
    </div>
  );
};

const App: React.FC = () => {
  const [started, setStarted] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  
  const domain = process.env.REACT_APP_AUTH0_DOMAIN || 'studioqdesigns.ca.auth0.com';
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID || 'nsLNFiwKfBAxumAYbPtLuOA5GgLVikzb';

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/admin`,
        // Must match the Matchset API identifier in Auth0 so the server can verify tokens
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      }}
      cacheLocation="localstorage"
      onRedirectCallback={(appState) => {
        window.history.replaceState(
          {},
          document.title,
          appState?.returnTo || '/admin'
        );
      }}
    >
      <Router>
        <Routes>
          <Route path="/admin" element={<AdminPage />} />
          <Route
            path="/"
            element={
              started ? (
                <GameRoute onFeedbackClick={() => setShowFeedbackModal(true)} />
              ) : (
                <LandingPage
                  onStart={() => setStarted(true)}
                  onFeedbackClick={() => setShowFeedbackModal(true)}
                />
              )
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
      
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />
    </Auth0Provider>
  );
};

export default App;
